import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Calendar, ArrowRight, Activity, Play, LayoutGrid, ListFilter, Trash2, AlertTriangle, Plane } from 'lucide-react';
import { projectsService, Project } from '../services/projects';
import { simulationsService, Simulation } from '../services/simulations';

import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';

export const SimulationHomePage = () => {
    const navigate = useNavigate();
    const { theme } = useTheme();
    const { language } = useLanguage();

    const [projects, setProjects] = useState<Project[]>([]);
    const [simulations, setSimulations] = useState<Simulation[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState<'date' | 'name'>('date');
    const [deleteId, setDeleteId] = useState<string | null>(null);

    const isDark = theme === 'dark';
    const textMain = 'text-[var(--text-primary)]';
    const textSub = 'text-[var(--text-secondary)]';
    const cardBg = isDark ? 'bg-[var(--surface)] border-[var(--border)]' : 'bg-white border-[var(--border)] shadow-sm hover:shadow-md';

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            // Load projects and simulations in parallel
            const [projectsData, simulationsData] = await Promise.all([
                projectsService.getAll(),
                simulationsService.getAll()
            ]);

            setProjects(projectsData);

            // Enrich simulations with project info
            const enrichedSimulations = simulationsData.map(sim => {
                const project = projectsData.find(p => p.id === sim.project_id);
                return {
                    ...sim,
                    project_name: project?.name,
                    project_iata: project?.iata_code,
                };
            });

            setSimulations(enrichedSimulations as any);
        } catch (error) {
            console.error("Error loading data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteId) return;
        try {
            await simulationsService.delete(deleteId);
            setSimulations(prev => prev.filter(s => s.id !== deleteId));
        } catch (error) {
            console.error("Error deleting simulation:", error);
        }
        setDeleteId(null);
    };

    const getScenarioColor = (type: string) => {
        switch (type) {
            case 'AMS': return 'bg-emerald-500';
            case 'FASTGATE': return 'bg-blue-500';
            case 'INFRA': return 'bg-purple-500';
            case 'CUSTOM': return 'bg-orange-500';
            default: return 'bg-gray-500';
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'COMPLETED': return { color: 'text-green-500', text: language === 'es' ? 'COMPLETADO' : 'COMPLETED' };
            case 'RUNNING': return { color: 'text-blue-500', text: language === 'es' ? 'EJECUTANDO' : 'RUNNING' };
            case 'ERROR': return { color: 'text-red-500', text: 'ERROR' };
            default: return { color: 'text-yellow-500', text: language === 'es' ? 'PENDIENTE' : 'PENDING' };
        }
    };

    // Status-based border colors
    const getStatusBorderColor = (status: string) => {
        switch (status) {
            case 'COMPLETED': return 'border-emerald-500';
            case 'RUNNING': return 'border-blue-500';
            case 'ERROR': return 'border-red-500';
            case 'PENDING': return 'border-[#F26430]';
            default: return 'border-red-500'; // No analysis/no simulation
        }
    };

    const processedSimulations = simulations
        .filter(s => {
            const search = searchTerm.toLowerCase();
            return (
                s.name.toLowerCase().includes(search) ||
                s.project_name?.toLowerCase().includes(search) ||
                s.project_iata?.toLowerCase().includes(search)
            );
        })
        .sort((a, b) => {
            if (sortBy === 'date') return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
            return a.name.localeCompare(b.name);
        });

    return (
        <div className="p-8 font-sans relative min-h-full">

            {/* MODAL BORRADO */}
            {deleteId && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center backdrop-blur-sm p-4">
                    <div className={`p-6 rounded-xl border border-red-500/30 w-full max-w-md text-center shadow-2xl ${cardBg}`}>
                        <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-500/20">
                            <AlertTriangle className="w-8 h-8 text-red-500" />
                        </div>
                        <h3 className={`text-xl font-bold mb-2 ${textMain}`}>
                            {language === 'es' ? '¿Eliminar esta simulación?' : 'Delete this simulation?'}
                        </h3>
                        <div className="flex justify-center gap-4 mt-6">
                            <button onClick={() => setDeleteId(null)} className={`px-4 py-2 rounded-lg ${textSub} hover:bg-gray-500/10`}>
                                {language === 'es' ? 'Cancelar' : 'Cancel'}
                            </button>
                            <button onClick={handleDeleteConfirm} className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg shadow-lg">
                                {language === 'es' ? 'Sí, Eliminar' : 'Yes, Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto">

                {/* HEADER */}
                <div className={`flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10 border-b pb-8 ${isDark ? 'border-[#2a2a2a]' : 'border-gray-300'}`}>
                    <div>
                        <h1 className={`text-4xl font-extrabold flex items-center gap-3 ${textMain}`}>
                            <Play className="w-8 h-8 text-emerald-500" />
                            {language === 'es' ? 'Simulador FTS' : 'FTS Simulator'}
                        </h1>
                        <p className={`text-sm mt-2 tracking-wide font-medium ${textSub}`}>
                            {language === 'es'
                                ? 'Fast Time Simulation - Escenarios What-If para análisis DACAMS'
                                : 'Fast Time Simulation - What-If Scenarios for DACAMS Analysis'}
                        </p>
                    </div>

                    <button
                        onClick={() => navigate('/fts/new')}
                        className="flex items-center justify-center gap-2 bg-[#F26430] hover:bg-[#d14f1f] text-white px-6 py-3 rounded-lg font-bold shadow-lg transition-transform hover:scale-105 active:scale-95"
                    >
                        <Plus size={18} /> {language === 'es' ? 'Nueva Simulación' : 'New Simulation'}
                    </button>
                </div>

                {/* BARRA DE HERRAMIENTAS */}
                <div className="flex flex-col md:flex-row gap-4 mb-8 max-w-4xl">
                    <div className={`flex-1 p-1 rounded-xl shadow-sm border flex items-center ${cardBg}`}>
                        <div className="pl-4 pr-2 text-[var(--text-secondary)]"><Search className="h-5 w-5" /></div>
                        <input
                            type="text"
                            placeholder={language === 'es' ? 'Buscar por nombre o proyecto...' : 'Search by name or project...'}
                            className={`w-full py-2 bg-transparent placeholder-gray-400 focus:outline-none text-sm ${textMain}`}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className={`w-full md:w-64 p-1 rounded-xl shadow-sm border flex items-center px-4 ${cardBg}`}>
                        <ListFilter className="w-4 h-4 text-emerald-500 mr-2" />
                        <span className={`text-xs font-bold uppercase mr-2 ${textSub}`}>{language === 'es' ? 'ORDENAR' : 'SORT'}:</span>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value as any)}
                            className={`bg-transparent outline-none text-sm font-bold cursor-pointer ${textMain} w-full`}
                        >
                            <option value="date" className="text-black">{language === 'es' ? 'Última Modif.' : 'Last Modified'}</option>
                            <option value="name" className="text-black">{language === 'es' ? 'Nombre (A-Z)' : 'Name (A-Z)'}</option>
                        </select>
                    </div>
                </div>

                {/* GRID DE SIMULACIONES */}
                {loading ? (
                    <div className="flex justify-center py-32"><div className="animate-spin rounded-full h-12 w-12 border-t-4 border-emerald-500"></div></div>
                ) : processedSimulations.length === 0 ? (
                    <div className={`flex flex-col items-center justify-center py-24 rounded-2xl border-2 border-dashed ${isDark ? 'border-[var(--border)] bg-[var(--surface-raised)]0' : 'border-gray-300 bg-gray-50'}`}>
                        <Play className="h-12 w-12 text-[var(--text-secondary)] mb-4" />
                        <p className={textSub}>
                            {language === 'es'
                                ? 'No hay simulaciones. Crea una nueva o completa un análisis DACAMS primero.'
                                : 'No simulations. Create a new one or complete a DACAMS analysis first.'}
                        </p>
                        <button
                            onClick={() => navigate('/home')}
                            className={`mt-4 text-sm ${isDark ? 'text-emerald-400 hover:text-emerald-300' : 'text-emerald-600 hover:text-emerald-700'}`}
                        >
                            {language === 'es' ? '→ Ir al HUB DACAMS' : '→ Go to DACAMS HUB'}
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
                        {processedSimulations.map((sim) => (
                            <div
                                key={sim.id}
                                onClick={() => navigate(`/fts/${sim.id}`)}
                                className={`group rounded-xl shadow-lg hover:shadow-2xl transition-all cursor-pointer overflow-hidden relative flex flex-col ${cardBg}`}
                            >
                                {/* Status color bar at top - like DACAMS HUB */}
                                <div className={`h-1 w-full ${sim.status === 'COMPLETED' ? 'bg-emerald-500' : sim.status === 'RUNNING' ? 'bg-blue-500' : sim.status === 'FAILED' ? 'bg-red-500' : 'bg-[#F26430]'}`} />

                                <button
                                    onClick={(e) => { e.stopPropagation(); setDeleteId(sim.id); }}
                                    className="absolute top-3 right-3 p-2 rounded-full bg-black/20 hover:bg-red-500 text-white transition-colors z-10 opacity-0 group-hover:opacity-100"
                                >
                                    <Trash2 size={14} />
                                </button>

                                <div className="p-6 flex-1">
                                    <div className="flex items-center gap-4 mb-4">
                                        <div className={`h-12 w-12 rounded-lg flex items-center justify-center font-mono font-bold text-lg shadow-inner ${isDark ? 'bg-[var(--surface-raised)] text-[var(--text-primary)] border border-[var(--border)]' : 'bg-gray-100 text-[#1C1C1C] border border-[var(--border)]'}`}>
                                            {sim.project_iata || <Plane size={20} />}
                                        </div>
                                        <div>
                                            <h3 className={`font-bold text-lg line-clamp-1 ${textMain}`}>{sim.name}</h3>
                                            <p className="text-xs font-mono text-[var(--text-tertiary)] bg-opacity-10 bg-gray-500 px-2 py-0.5 rounded inline-block mt-1">
                                                {sim.scenario_type}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="space-y-2 mt-4 pt-4 border-t border-gray-500/20">
                                        <div className="flex justify-between text-xs">
                                            <span className={`flex items-center gap-2 ${textSub}`}><LayoutGrid size={14} /> {language === 'es' ? 'Proyecto' : 'Project'}</span>
                                            <span className={`font-mono ${textMain}`}>{sim.project_name || '-'}</span>
                                        </div>
                                        <div className="flex justify-between text-xs">
                                            <span className={`flex items-center gap-2 ${textSub}`}><Calendar size={14} /> {language === 'es' ? 'Creado' : 'Created'}</span>
                                            <span className={`font-mono ${textMain}`}>{sim.created_at ? new Date(sim.created_at).toLocaleDateString() : '-'}</span>
                                        </div>
                                        <div className="flex justify-between text-xs">
                                            <span className={`flex items-center gap-2 ${textSub}`}><Activity size={14} /> {language === 'es' ? 'Estado' : 'Status'}</span>
                                            <span className={`font-bold ${getStatusBadge(sim.status).color}`}>
                                                {getStatusBadge(sim.status).text}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className={`px-6 py-3 border-t flex justify-between items-center group-hover:bg-emerald-500/5 transition-colors ${isDark ? 'bg-[var(--surface)] border-[var(--border)]' : 'bg-gray-50 border-[var(--border)]'}`}>
                                    <span className={`text-[10px] font-bold uppercase tracking-widest ${textSub}`}>
                                        {language === 'es' ? 'Ver Simulación' : 'View Simulation'}
                                    </span>
                                    <ArrowRight className="h-4 w-4 text-emerald-500 transform group-hover:translate-x-1 transition-transform" />
                                </div>
                            </div>
                        ))}
                    </div>
                )}

            </div>
        </div>
    );
};

export default SimulationHomePage;
